import { DataTable, useDataTableContext } from "@msuite/ui/table";
import { guardArray, uniqueArray } from "@msuite/utils/array";
import type { SubstitutionPlanFrontendFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { getFullName } from "@msuite/utils/users";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { UserCard } from "@/components/cards";
import { useAuth } from "@/hooks/use-auth";
import { useMembers } from "@/hooks/use-members";
import { useSubstitutionPlanFormContext } from "./context";

/** Props Interface */
interface SubstitutionPlanSelectProps {
	itemId: string;
}

export function SubstitutionPlanSelect({ itemId }: SubstitutionPlanSelectProps) {
	const { getSelectedRowIds } = useDataTableContext();
	const { getItemIndex } = useSubstitutionPlanFormContext();
	const methods = useFormContext<SubstitutionPlanFrontendFormSchema>();
	const { members: _members } = useMembers({ capabilities: ["manage-project"] });
	const index = getItemIndex(itemId);
	const { user } = useAuth();

	const isNotRequired = useWatch({
		control: methods.control,
		name: `items.${index}.isAssignmentNotRequired`,
	});

	function handleOnBatchSelect(memberIds: string[]) {
		const ids = uniqueArray([itemId].concat(getSelectedRowIds()));
		for (const id of ids) {
			const index = getItemIndex(id);
			const path = `items.${index}.assignedUserIds` as const;
			methods.setValue(path, memberIds, { shouldDirty: true });
		}
	}

	const members = useMemo(() => {
		return guardArray(_members).filter((m) => m.id !== user?.id);
	}, [_members]);

	if (isNotRequired) return guardString(null);

	/** Render */
	return (
		<DataTable.Select
			multiple
			max={1}
			methods={methods}
			name={`items.${index}.assignedUserIds`}
			onChange={(memberIds) => {
				handleOnBatchSelect(guardArray(memberIds as string[]).slice(0, 1));
			}}
			options={
				members?.map((member) => ({
					label: `${member.user.firstName} ${member.user.lastName}`,
					value: member.id,
					item: member,
				})) ?? []
			}
			renderOption={(item) => (
				<UserCard
					variant="plain"
					user={item.user}
					userId={item.id}
					withPopover={false}
				/>
			)}
			multipleSelectionRender={(items) => {
				const labels = items.map((item) => getFullName(item.item?.user)).join(", ");
				return <p>{labels}</p>;
			}}
			isDisabled={isNotRequired}
		/>
	);
}
